import type { CalendarDayInfo } from './types.js';

export interface CalendarMonthPreviewProps {
  readonly year: number;
  /** 1–12。 */
  readonly month: number;
  /** 該月每一天的解析結果，由呼叫端算好傳入（本元件零運算依賴）。 */
  readonly days: readonly CalendarDayInfo[];
  readonly onPrevMonth: () => void;
  readonly onNextMonth: () => void;
}

const WEEKDAY_HEADERS = ['日', '一', '二', '三', '四', '五', '六'] as const;

const STATUS_STYLE: Record<CalendarDayInfo['status'], string> = {
  working: 'bg-emerald-50 text-emerald-800 dark:bg-ok-soft dark:text-ok',
  'non-working': 'bg-gray-100 text-gray-400 dark:bg-raised dark:text-fg-subtle',
  'exception-working': 'bg-amber-100 text-amber-800 dark:bg-warn-soft dark:text-warn',
  'exception-non-working': 'bg-rose-100 text-rose-700 dark:bg-danger-soft dark:text-danger',
};

const STATUS_LABEL: Record<CalendarDayInfo['status'], string> = {
  working: '工作日',
  'non-working': '非工作日',
  'exception-working': '例外（工作）',
  'exception-non-working': '例外（假日）',
};

/**
 * 月曆格狀預覽：把工作日／非工作日／例外日用不同底色標出。
 * 純展示元件：props in / callbacks out。零 store、零 Tauri 相依、零日曆運算。
 */
export function CalendarMonthPreview({
  year,
  month,
  days,
  onPrevMonth,
  onNextMonth,
}: CalendarMonthPreviewProps): React.JSX.Element {
  const first = days[0];
  const leadingBlanks = first !== undefined ? new Date(`${first.date}T00:00:00`).getDay() : 0;

  return (
    <div data-testid="calendar-month-preview" className="text-sm">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label="上個月"
          data-testid="calendar-preview-prev"
          className="rounded px-2 py-1 text-fg-muted hover:bg-raised"
          onClick={onPrevMonth}
        >
          ‹
        </button>
        <span className="font-medium text-fg">
          {year} 年 {month} 月
        </span>
        <button
          type="button"
          aria-label="下個月"
          data-testid="calendar-preview-next"
          className="rounded px-2 py-1 text-fg-muted hover:bg-raised"
          onClick={onNextMonth}
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs text-fg-muted">
        {WEEKDAY_HEADERS.map((h) => (
          <div key={h} className="pb-1">
            {h}
          </div>
        ))}
        {Array.from({ length: leadingBlanks }, (_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {days.map((day) => (
          <div
            key={day.date}
            data-testid={`calendar-preview-day-${day.date}`}
            title={`${day.date} ${STATUS_LABEL[day.status]}${day.exceptionName !== null ? `：${day.exceptionName}` : ''}${
              day.timeRanges.length > 0 ? `\n${day.timeRanges.join('、')}` : ''
            }`}
            className={`flex flex-col items-center justify-center rounded py-1.5 ${STATUS_STYLE[day.status]}`}
          >
            <span>{Number(day.date.slice(-2))}</span>
            {day.exceptionName !== null ? (
              <span className="max-w-full truncate px-0.5 text-[10px] leading-tight">{day.exceptionName}</span>
            ) : null}
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-3 text-xs text-fg-muted">
        {(Object.keys(STATUS_LABEL) as CalendarDayInfo['status'][]).map((status) => (
          <span key={status} className="flex items-center gap-1">
            <span className={`inline-block h-3 w-3 rounded ${STATUS_STYLE[status]}`} />
            {STATUS_LABEL[status]}
          </span>
        ))}
      </div>
    </div>
  );
}
